import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'lodash';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import {
  TextField,
  Button,
  InputLabel,
  FormControl,
  FormHelperText,
  FormGroup,
  Select as MuiSelect,
  Checkbox
} from '@material-ui/core';
import { SelectMultiField } from '../../common';
import { setData } from '../../../redux/dataSlice';
import { baseApiUrl } from '../../../config';

const checkBoxStyles = {
  margin: '8px 0px'
};

const AxsomeRegFormComponent = (props) => {
  // Alias the injected setData action so it doesn't shadow the module-level
  // import used in the connect() call at the bottom of the file.
  const {
    dropDownValues,
    isLicensed,
    npiInfo,
    meetingInfo,
    setData: dispatchSetData,
    regSource,
    registrantType
  } = props;

  const isAPR = registrantType === 'apr';

  const [formValues, setFormValues] = React.useState({
    meeting_id: meetingInfo.id,
    reg_source: regSource
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [npiDataObj, setNpiDataObj] = React.useState({});
  const [attendanceType, setAttendanceType] = React.useState(null);

  const categories = isLicensed
    ? dropDownValues.hcpAttendeeSubTypes
    : dropDownValues.attendeeSubTypes;

  React.useEffect(() => {
    if (!_.isEmpty(npiInfo)) {
      let npiData;
      if (_.keys(npiInfo).includes('basic')) {
        // Data coming from the NPI registry (standard, non-contact-specific flow)
        const { basic, addresses, taxonomies } = npiInfo;
        npiData = {
          npi: npiInfo.number,
          firstname: basic.first_name,
          lastname: basic.last_name,
          minitial: basic.middle_name ? basic.middle_name[0] : '',
          address1: addresses[0].address_1 || '',
          address2: addresses[0].address_2 || '',
          city: addresses[0].city || '',
          state: addresses[0].state || '',
          zip: addresses[0].postal_code || '',
          ...computeTagCloudValues(
            _.compact(taxonomies.map((taxonomy) => taxonomy.desc)),
            dropDownValues.specialties,
            'specialty'
          ),
          ...computeTagCloudValues(_.compact([basic.credential]), dropDownValues.degrees, 'degree')
        };
      } else {
        // Data coming from the contact code lookup (contact-specific meetings)
        npiData = {
          contactId: npiInfo.contactid,
          npi: npiInfo.npinumber,
          firstname: npiInfo.firstname,
          lastname: npiInfo.lastname,
          minitial: npiInfo.middleinitial,
          orgname: npiInfo.orgname,
          address1: npiInfo.orgaddress1,
          address2: npiInfo.orgaddress2,
          phone: npiInfo.phone,
          city: npiInfo.orgcity,
          state: npiInfo.orgstate,
          zip: npiInfo.orgpostalcode,
          email: npiInfo.email,
          licensed_state: npiInfo.licensedstate,
          license_number: npiInfo.statelicensenum,
          ...computeTagCloudValues(
            _.compact((npiInfo.specialty || '').split(',').map((s) => s.trim())),
            dropDownValues.specialties,
            'specialty'
          ),
          ...computeTagCloudValues(
            _.compact((npiInfo.medicaldegree || '').split(',').map((d) => d.trim())),
            dropDownValues.degrees,
            'degree'
          )
        };
      }
      setNpiDataObj(npiData);
      setFormValues((prev) => ({ ...prev, ...npiData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computeTagCloudValues = (incomingValues, incomingdropDownValues, objectKey) => {
    const tagArray = [];
    let otherElem = '';
    let otherTagFlag = false;
    if (incomingValues && incomingValues.length > 0) {
      const otherDropDownElem = _.find(
        incomingdropDownValues,
        (elem) => elem.name.toUpperCase() === 'OTHER'
      );

      incomingValues.forEach((elemName) => {
        if (elemName.toUpperCase() !== 'OTHER') {
          const dropdownElem = _.find(
            incomingdropDownValues,
            (elem) => elem.name.toUpperCase() === elemName.toUpperCase()
          );
          const valueName = dropdownElem && dropdownElem.name;
          if (valueName) {
            tagArray.push({ value: valueName, label: valueName });
          } else if (otherDropDownElem) {
            if (!otherTagFlag) {
              tagArray.push({
                value: otherDropDownElem.name,
                label: otherDropDownElem.name
              });
              otherTagFlag = true;
            }
            otherElem = otherElem.length ? (otherElem += `, ${elemName}`) : elemName;
          }
        }
      });
    }
    return { [objectKey]: tagArray, [`other_${objectKey}`]: otherElem };
  };

  const validateFields = () => {
    let requiredFields = [
      'firstname',
      'lastname',
      'orgname',
      'address1',
      'city',
      'state',
      'zip',
      'email'
    ];

    if (isLicensed) {
      // Prescribing HCP
      requiredFields = requiredFields.concat(['specialty', 'degree', 'licensed_state']);
    } else {
      // Non-Prescribing HCP
      requiredFields.push('role');
    }

    const errors = {};
    requiredFields.forEach((field) => {
      const value = formValues[field];
      const isEmpty = _.isArray(value) ? value.length === 0 : !value;
      if (isEmpty) {
        errors[field] = true;
      }
    });

    if (formValues.npi && isLicensed) {
      const npiLen = formValues.npi.toString().length;
      if (parseInt(npiLen, 10) !== 10) {
        errors.npi = true;
      }
    }

    if (formValues.email) {
      if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(formValues.email)) {
        errors.email = true;
      }
    }

    if (formValues.apr_email) {
      if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(formValues.apr_email)) {
        errors.apr_email = true;
      }
    }

    if (meetingInfo.is_virtual_hybrid && !attendanceType) {
      errors.attendanceType = true;
    }

    console.log('errors', errors);
    setFormErrors(errors);
    return !!_.isEmpty(errors);
  };

  const resetRegForm = () => {
    setFormValues({ reg_source: regSource });
    setFormErrors({});
    setNpiDataObj({});
  };

  const submitForm = async () => {
    const isValid = validateFields();
    formValues.isLicensed = isLicensed;

    if (isValid) {
      setSubmitting(true);
      setSubmitError(null);
      const formPayload = { ...formValues };

      formPayload.specialty = _.map(formPayload.specialty || [], 'value').join(', ');
      if (formPayload.other_specialty) {
        formPayload.specialty = `${formPayload.specialty}, ${formPayload.other_specialty}`;
      }
      formPayload.degree = _.map(formPayload.degree || [], 'value').join(', ');
      if (formPayload.other_degree) {
        formPayload.degree = `${formPayload.degree}, ${formPayload.other_degree}`;
      }

      if (formPayload.role?.toUpperCase() === 'OTH') {
        formPayload.role = formPayload.other_role;
      }

      if (meetingInfo.is_virtual_hybrid) {
        formPayload.is_virtual_attendee = attendanceType === 'virtual' ? 1 : 0;
      }

      const req = await fetch(`${baseApiUrl}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formPayload, contactId: npiInfo.contactid })
      });
      setSubmitting(false);
      if (req.status === 201) {
        setSubmitted(true);
      } else if (req.status === 403) {
        setSubmitError('This meeting is at capacity.');
      } else if (req.status === 401) {
        setSubmitError('You are already registered for this meeting.');
      } else {
        setSubmitError('Something went wrong with your submission.');
      }
    }
  };

  const updateFormField = (field) => {
    return (event) => {
      if (['email_consent'].includes(field)) {
        return setFormValues({ ...formValues, [field]: event.target.checked });
      }

      let { value } = event.target;

      if (field === 'npi') {
        value = value.replace(/\D/g, '').slice(0, 10);
      }

      setFormValues({ ...formValues, [field]: value });
    };
  };

  const onVirtualHybridChange = (label) => {
    return (e) => {
      if (e.target.checked) {
        setAttendanceType(label);
      }
    };
  };

  const updateSelectMultiple = (field) => {
    return (values) => {
      setFormValues({ ...formValues, [field]: values });
    };
  };

  const renderNpiFields = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex' }}>
          <TextField
            disabled={Boolean(npiInfo && /^\d{10}$/.test(npiDataObj.npi))}
            fullWidth
            style={{ margin: 10 }}
            value={formValues.npi || ''}
            onChange={updateFormField('npi')}
            label={'*NPI'}
            error={formErrors.npi}
            helperText={formErrors.npi && 'NPI needs to be 10 digits.'}
            margin={'dense'}
            variant={'outlined'}
            type="text"
          />
        </div>
        <div style={{ width: '50%', display: 'flex' }} className="mobile-portrait-width-unset">
          <FormControl fullWidth style={{ margin: 10 }}>
            <InputLabel htmlFor="licensed_state" error={formErrors.licensed_state}>
              *Licensed State
            </InputLabel>
            <MuiSelect
              native
              variant={'outlined'}
              value={formValues.licensed_state || ''}
              onChange={updateFormField('licensed_state')}
              margin={'dense'}
              label="*Licensed State"
              error={formErrors.licensed_state}
              inputProps={{
                name: 'licensed_state',
                id: 'licensed_state',
                maxLength: 2
              }}
            >
              {_.map(dropDownValues.states, (state) => {
                return (
                  <option key={state.abrv} value={state.abrv}>
                    {state.name}
                  </option>
                );
              })}
            </MuiSelect>
          </FormControl>
          <TextField
            fullWidth
            style={{ margin: 10 }}
            value={formValues.license_number || ''}
            onChange={updateFormField('license_number')}
            label={'License #'}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 30 }}
          />
        </div>
      </div>
    );
  };

  if (submitted) {
    return (
      <div style={{ marginTop: 40 }}>
        <h2>Thank you for registering!</h2>
        <div style={{ fontSize: 20, marginTop: 40 }}>
          <p style={{ fontWeight: 800 }}>
            Participant Name: {formValues.firstname} {formValues.lastname}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: 20, marginTop: 40 }}>
          {/* <Button
            variant="contained"
            className="hlx-button"
            style={{ width: 300 }}
            onClick={() => {
              resetRegForm();
              window.location.href = `/${meetingInfo.invitationcode}`;
            }}
          >
            Add another registrant
          </Button> */}
          <Button
            variant="contained"
            className="hlx-button"
            style={{ width: 300 }}
            onClick={() => {
              resetRegForm();
              window.location.href = '/';
            }}
          >
            Register for a different event
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Name fields - locked from contact lookup */}
        <div style={{ display: 'flex' }}>
          <TextField
            fullWidth
            disabled={Boolean(npiDataObj.firstname)}
            style={{ margin: 10 }}
            value={formValues.firstname || ''}
            onChange={updateFormField('firstname')}
            label={'*First Name'}
            margin={'dense'}
            variant={'outlined'}
            error={formErrors.firstname}
            inputProps={{ maxLength: 35 }}
          />
          <TextField
            fullWidth
            disabled={Boolean(npiDataObj.minitial)}
            style={{ margin: 10 }}
            value={formValues.minitial || ''}
            onChange={updateFormField('minitial')}
            label={'Middle Name'}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 1 }}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <TextField
            fullWidth
            disabled={Boolean(npiDataObj.lastname)}
            style={{ margin: 10 }}
            value={formValues.lastname || ''}
            onChange={updateFormField('lastname')}
            label={'*Last Name'}
            margin={'dense'}
            variant={'outlined'}
            error={formErrors.lastname}
            inputProps={{ maxLength: 35 }}
          />
        </div>

        {/* Prescribing HCP fields */}
        {isLicensed && (
          <>
            {renderNpiFields()}
            <div style={{ display: 'flex' }}>
              <TextField
                fullWidth
                style={{ margin: 10 }}
                value={formValues.prodesignation || ''}
                onChange={updateFormField('prodesignation')}
                label={'Professional Designation/Title'}
                margin={'dense'}
                variant={'outlined'}
                inputProps={{ maxLength: 50 }}
              />
            </div>
            <div
              style={{ display: 'flex', marginTop: 10 }}
              className="mobile-column-reverse-switch"
            >
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <FormControl style={{ marginLeft: 10, marginRight: 10, display: 'block' }}>
                  <SelectMultiField
                    values={formValues.specialty}
                    label={'*Specialty'}
                    options={_.map(dropDownValues.specialties, (specialty) => ({
                      value: specialty.name,
                      label: specialty.name
                    }))}
                    onChange={updateSelectMultiple('specialty')}
                    error={formErrors.specialty}
                  />
                </FormControl>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <FormControl style={{ display: 'block', margin: '2px 10px 5px 10px' }}>
                  <SelectMultiField
                    values={formValues.degree}
                    label={'*Degree'}
                    options={_.map(dropDownValues.degrees, (degree) => ({
                      value: degree.name,
                      label: degree.name
                    }))}
                    onChange={updateSelectMultiple('degree')}
                    error={formErrors.degree}
                  />
                </FormControl>
              </div>
            </div>
          </>
        )}

        {/* Non-Prescribing HCP: Role dropdown */}
        {!isLicensed && (
          <div className="mobile-column-switch">
            <div style={{ padding: 10, width: '50%' }} className="mobile-role-width-switch">
              <FormControl fullWidth>
                <InputLabel error={formErrors.role} htmlFor="role">
                  *Role
                </InputLabel>
                <MuiSelect
                  native
                  variant={'outlined'}
                  value={formValues.role}
                  onChange={updateFormField('role')}
                  error={formErrors.role}
                  label="*Role"
                  margin={'dense'}
                  inputProps={{
                    name: 'role',
                    id: 'role',
                    maxLength: 50
                  }}
                >
                  <option aria-label="None" key="none" value="" />
                  {_.map(categories, (role) => {
                    return (
                      <option key={role.subcatid} value={role.subcatcode}>
                        {role.name}
                      </option>
                    );
                  })}
                </MuiSelect>
              </FormControl>
            </div>
          </div>
        )}

        {/* Common fields for both */}
        <div style={{ display: 'flex' }}>
          <TextField
            fullWidth
            style={{ margin: 10 }}
            value={formValues.orgname || ''}
            onChange={updateFormField('orgname')}
            label={'*Affiliation'}
            error={formErrors.orgname}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 100 }}
          />
        </div>

        <div style={{ display: 'flex' }}>
          <TextField
            fullWidth
            style={{ margin: 10 }}
            value={formValues.address1 || ''}
            onChange={updateFormField('address1')}
            label={'*Office Address'}
            error={formErrors.address1}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            fullWidth
            style={{ margin: 10 }}
            value={formValues.address2 || ''}
            onChange={updateFormField('address2')}
            label={'Address 2'}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 50 }}
          />
        </div>
        <div className="flex mobile-column-switch">
          <TextField
            style={{ margin: 10, minWidth: '30%' }}
            value={formValues.city || ''}
            onChange={updateFormField('city')}
            label={'*City'}
            error={formErrors.city}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 50 }}
          />
          <div className="flex" style={{ minWidth: '50%' }}>
            <FormControl style={{ margin: 10, flex: '1 50px' }}>
              <InputLabel error={formErrors.state} htmlFor="state">
                *State
              </InputLabel>
              <MuiSelect
                native
                variant={'outlined'}
                value={formValues.state || ''}
                onChange={updateFormField('state')}
                error={formErrors.state}
                margin={'dense'}
                label="*State"
                inputProps={{
                  name: 'state',
                  id: 'state',
                  maxLength: 2
                }}
              >
                {_.map(dropDownValues.states, (state) => {
                  return (
                    <option key={state.abrv} value={state.abrv}>
                      {state.name}
                    </option>
                  );
                })}
              </MuiSelect>
            </FormControl>
            <TextField
              fullWidth
              style={{ margin: 10, flex: '1 100px' }}
              value={formValues.zip || ''}
              onChange={updateFormField('zip')}
              label={'*Zip'}
              error={formErrors.zip}
              margin={'dense'}
              variant={'outlined'}
              inputProps={{ minLength: 5, maxLength: 10 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          <TextField
            fullWidth
            style={{ margin: 10 }}
            value={formValues.phone || ''}
            onChange={updateFormField('phone')}
            label={'Phone'}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 15 }}
            type="tel"
          />
          <TextField
            fullWidth
            style={{ margin: 10 }}
            value={formValues.email || ''}
            onChange={updateFormField('email')}
            label={'*Email'}
            error={formErrors.email}
            helperText={formErrors.email && 'Invalid email address'}
            margin={'dense'}
            variant={'outlined'}
            inputProps={{ maxLength: 100 }}
            type="email"
          />
        </div>

        {/* APR email - only if Authorized Representative is registering */}
        {isAPR && (
          <div style={{ display: 'flex' }}>
            <TextField
              fullWidth
              style={{ margin: 10 }}
              value={formValues.apr_email || ''}
              onChange={updateFormField('apr_email')}
              label={'Email of Authorized Representative'}
              error={formErrors.apr_email}
              helperText={formErrors.apr_email && 'Invalid email address'}
              margin={'dense'}
              variant={'outlined'}
              inputProps={{ maxLength: 100 }}
              type="email"
            />
          </div>
        )}

        <div style={{ padding: 10 }}>
          <TextField
            className="multiLineTextField"
            fullWidth
            multiline
            rows={3}
            value={formValues.dietary_constraints || ''}
            onChange={updateFormField('dietary_constraints')}
            InputLabelProps={{
              style: { fontSize: 14, paddingRight: 5 }
            }}
            label={'Dietary Constraints'}
            variant={'outlined'}
            inputProps={{ maxLength: 50 }}
          />
        </div>
        <div style={{ padding: 10 }}>
          <TextField
            className="multiLineTextField"
            fullWidth
            multiline
            rows={3}
            value={formValues.comments || ''}
            InputLabelProps={{
              style: { fontSize: 14 }
            }}
            onChange={updateFormField('comments')}
            label={'Comments'}
            variant={'outlined'}
            inputProps={{ maxLength: 100 }}
          />
        </div>
        <div style={{ paddingLeft: 10, marginBottom: 20 }}>
          <FormControl style={checkBoxStyles}>
            <FormControlLabel
              control={
                <Checkbox
                  color="default"
                  checked={formValues.email_consent || false}
                  onChange={updateFormField('email_consent')}
                  name="email_consent"
                />
              }
              label="I give my consent to use my email address for communications involving this event."
            />
          </FormControl>
        </div>

        {meetingInfo.is_virtual_hybrid && (
          <div style={{ paddingLeft: 10, marginBottom: 20 }}>
            <FormControl required error={formErrors.attendanceType} style={checkBoxStyles}>
              <span style={{ maxWidth: 750 }}>
                The program you are registering for is hybrid and can accommodate both in-person and
                virtual attendees. <br />
                Please confirm how you are planning to attend by choosing from the options below.*
              </span>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={attendanceType === 'in-person'}
                      onChange={onVirtualHybridChange('in-person')}
                      name="is_virtual_hybrid"
                    />
                  }
                  label="In-person"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      checked={attendanceType === 'virtual'}
                      onChange={onVirtualHybridChange('virtual')}
                      name="is_virtual_hybrid"
                    />
                  }
                  label="Virtual"
                />
              </FormGroup>
              {formErrors.attendanceType && (
                <FormHelperText style={{ color: '#f44336' }}>
                  This field is required.
                </FormHelperText>
              )}
            </FormControl>
          </div>
        )}
      </div>
      {submitError && (
        <p style={{ color: 'red', textAlign: 'center', fontSize: 20 }}>{submitError}</p>
      )}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          style={{ width: '125px' }}
          className="hlx-button"
          variant="contained"
          disabled={submitting}
          onClick={submitForm}
        >
          Register
        </Button>
        <Button
          className="cancel-btn hlx-button"
          variant="contained"
          onClick={() => dispatchSetData({ npiInfo: null, isLicensed: null, registrantType: null })}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = ({ data }) => {
  const { dropDownValues, isLicensed, npiInfo, meetingInfo, regSource, registrantType } = data;
  return { dropDownValues, isLicensed, npiInfo, meetingInfo, regSource, registrantType };
};
export default withRouter(connect(mapStateToProps, { setData })(AxsomeRegFormComponent));
